import * as firebase from 'firebase/app';
import { firestore, database, analytics } from './firebase';
import { writable, Readable, derived, Unsubscriber } from '../libs/store';
import { uuid } from '../libs/uuid';
import { getNow } from './datetime';
import { RemoteSprint, Sprint, getStatus, Status } from './sprints';
import { user, User } from './user';
import { Delta } from '@typewriter/editor';
import { dehydrateDeltas, hydrateDeltas } from './hydration';
import { sanitizeDelta, sanitizeText } from '../editor';

const LOAD_SINCE = 1000 * 60 * 60 * 24;
const emptyArray = [];

interface Chat {
  uid: string;
  content: Delta;
}

interface ChatStore extends Readable<Chat[]> {
  send(content: Delta): Promise<void>;
}

interface RemoteSprintStore extends Readable<RemoteSprint> {
  save(sprint: RemoteSprint): Promise<RemoteSprint>;
}

interface PublicSprintsStore extends Readable<RemoteSprint[]> {
  save(sprint: RemoteSprint): Promise<RemoteSprint>;
}

interface UserLookup {
  [uid: string]: User;
}

interface UserLookupStore extends Readable<UserLookup> {
  lookup(uid: string): Promise<User>;
}

interface SprintCounts {
  [uid: string]: number;
}

interface SprintTotals {
  participants: number;
  total: number;
}

interface UserStore extends Readable<User> {

}

export const userLookup = createUserLookupStore();
export const publicSprints = createPublicSprintsStore();

user.subscribe(async user => {
  if (user) {
    const saved = await userLookup.lookup(user.uid);
    if (!saved || saved.name !== user.name) {
      if (!user) return;
      userLookup.get()[user.uid] = user;
      await firestore.collection('users').doc(user.uid).set(user);
    }
  }
});

export function createChatStore(user: UserStore, sprintIdStore: Readable<string>): ChatStore {
  let collection;
  let unsub;
  let uid;

  const { get, subscribe } = derived<Chat[], [UserStore, Readable<string>]>([user, sprintIdStore], ([ user, sprintId ], set) => {
    set(emptyArray);

    if (!user || !sprintId) {
      if (unsub) unsub();
    } else {
      uid = user.uid;
      collection = firestore.collection(`sprints/${sprintId}/chats`);
      const query = collection.orderBy('timestamp').limitToLast(40);
      unsub = query.onSnapshot(querySnapshot => {
        set(querySnapshot.docs.map(doc => doc.data()) as Chat[]);
      });
    }

    return () => unsub && unsub();
  });

  function send(content: Delta) {
    if (!collection) return;
    analytics.logEvent('send_chat');
    return collection.doc(uuid(12)).set({
      uid,
      content: sanitizeDelta(content).ops,
      timestamp: firebase.firestore.FieldValue.serverTimestamp(),
    });
  }

  return {
    send,
    get, subscribe
  }
}


export function createUserLookupStore(): UserLookupStore {
  const collection = firestore.collection('users');

  const { get, set, subscribe } = writable<UserLookup>({});

  function lookup(uid: string): Promise<User> {
    return new Promise((resolve, reject) => {
      collection.doc(uid).onSnapshot(snapshot => {
        const user = snapshot.data() as User;
        set({ ...get(), [snapshot.id]: user });
        resolve(user);
      }, err => {
        reject(err);
      });
    });
  }

  return {
    lookup,
    get, subscribe
  }
}


export function createFirebaseObjectStore(path: Readable<string>, defaultValue: any = {}, sub?: () => Unsubscriber): Readable<any> {

  const { get, subscribe } = derived<any, Readable<string>>(path, (path, set) => {
    set(defaultValue);
    if (!path) return;

    const ref = database.ref(path);
    let loaded = false;
    let unsub;
    if (sub) unsub = sub();

    const onLoad = (snapshot: firebase.database.DataSnapshot) => (loaded = true) && set(snapshot.val() || defaultValue);
    const onUpdate = (snapshot: firebase.database.DataSnapshot) => loaded && set({ ...get(), [snapshot.key]: snapshot.val() });
    ref.once('value', onLoad);
    ref.on('child_added', onUpdate);
    ref.on('child_changed', onUpdate);

    return () => {
      unsub && unsub();
      ref.off('value', onLoad);
      ref.off('child_added', onUpdate);
      ref.off('child_changed', onUpdate);
    }
  });

  return { get, subscribe };
}


export async function saveWordcount(sprintId: string, uid: string, wordcount: number) {
  if (!sprintId || sprintId.length !== 12 || !uid) return;
  await database.ref(`/counts/${sprintId}/${uid}`).set(wordcount);
}


export function createSprintTotalsStore(sprint: Readable<Sprint>, time: Readable<number>): Readable<SprintTotals> {
  const path = derived([ sprint, time ], ([ sprint ]) => {
    if (!sprint || sprint.id.length !== 12) return;
    const status = getStatus(sprint);
    if (status === Status.Pending) return;
    return `/totals/${sprint.id}`;
  });
  return createFirebaseObjectStore(path, { participants: 0, total: 0 });
}


export function createPublicSprintsStore(): PublicSprintsStore {
  const collection = firestore.collection('sprints');

  const { get, subscribe } = writable<RemoteSprint[]>([], set => {
    const query = collection.where('listed', '==', true).where('start', '>', getNow() - LOAD_SINCE).orderBy('start').limit(20);
    return query.onSnapshot(querySnapshot => {
      set(querySnapshot.docs.map(doc => doc.data()) as RemoteSprint[]);
    });
  });

  return {
    save: saveSprint,
    get, subscribe
  }
}


export function createMySprintsStore(user: UserStore): PublicSprintsStore {
  const collection = firestore.collection('sprints');
  let unsub: Unsubscriber;

  const { get, subscribe } = derived<RemoteSprint[], UserStore>(user, (user, set) => {
    set(emptyArray);

    if (!user) {
      if (unsub) unsub();
    } else {
      const query = collection.where('creator', '==', user.uid).where('start', '>', getNow() - LOAD_SINCE).orderBy('start').limit(20);
      unsub = query.onSnapshot(querySnapshot => {
        set(querySnapshot.docs.map(doc => doc.data()) as RemoteSprint[]);
      });
    }

    return () => unsub && unsub();
  });

  return {
    save: saveSprint,
    get, subscribe
  }
}


export function createRemoteSprintStore(sprintId: string = uuid(12)): RemoteSprintStore {
  const doc = firestore.doc(`sprints/${sprintId}`);

  const { get, subscribe } = writable<RemoteSprint>(null, set => {
    return doc.onSnapshot(snapshot => {
      set(snapshot.data() as RemoteSprint);
    });
  });

  function save(sprint: RemoteSprint) {
    if (!sprint.id) sprint.id = sprintId;
    if (sprint.id !== sprintId) throw new Error('The id does not match');
    return saveSprint(sprint);
  }

  return {
    save,
    get, subscribe
  }
}


export async function saveSprint(sprint: RemoteSprint) {
  if (!sprint.id) sprint.id = uuid(12);
  Object.keys(sprint).forEach(key => sprint[key] === undefined && delete sprint[key]);
  if (sprint.title) sprint.title = sanitizeText(sprint.title);
  if (sprint.prompt) sprint.prompt = sanitizeDelta(sprint.prompt);
  await firestore.doc(`sprints/${sprint.id}`).set(dehydrateDeltas(sprint));
  return sprint;
}

export async function deleteSprint(sprintId: string) {
  await firestore.doc(`sprints/${sprintId}`).delete();
}

export async function getSprint(sprintId: string) {
  const snapshot = await firestore.doc(`sprints/${sprintId}`).get();
  return hydrateDeltas(snapshot.data());
}

